import React from 'react';
import Link from 'next/link';
import { Slot } from '@components/common/Slot';
import { cn } from '@helpers/className';
import Paragraph from '@solar/common/Paragraph/Paragraph';
import styles from './TextLink.module.css';

/**
 * TextLink Component
 *
 * A customizable link component with animated underline and color transition on hover/focus.
 *
 * @component
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to be displayed inside the link
 * @param {string} props.href - URL to navigate to when the link is clicked
 * @param {string} [props.className] - Additional CSS classes to apply to the link
 * @param {string} [props.target="_blank"] - Target attribute for the link (e.g., _blank, _self)
 * @param {'default' | 'underlined'} [props.variant="default"] - Link style variant
 *   - default: No underline by default, animates underline from left to right on hover/focus,
 * removes from right to left
 *   - underlined: Shows underline by default,
 * animates underline removal from right to left on hover/focus
 * @param {'light' | 'dark'} [props.color="light"] - Color theme for the link
 *   - light: Text transitions from primary to tertiary noi color, underline matches text color
 *   - dark: Text transitions from inverted-primary to inverted-secondary,
 * underline matches text color
 *
 * @example
 * // Basic usage with default styling
 * <TextLink href="https://example.com">Visit Website</TextLink>
 *
 * @example
 * // Using the underlined variant
 * <TextLink href="https://example.com" variant="underlined">Underlined Link</TextLink>
 *
 * @example
 * // Dark color variant for use on dark backgrounds
 * <TextLink href="https://example.com" color="dark">Dark Theme Link</TextLink>
 *
 * @example
 * // Customizing target attribute
 * <TextLink href="https://example.com" target="_self">Open in Same Tab</TextLink>
 *
 * @example
 * // Combining multiple customizations
 * <TextLink
 *   href="https://example.com"
 *   variant="underlined"
 *   color="dark"
 *   className="my-custom-class"
 * >
 *   Customized Link
 * </TextLink>
 */
interface TextLinkProps {
  children: React.ReactNode;
  href: string;
  className?: string;
  variant?: 'default' | 'underlined';
  color?: 'light' | 'dark';
  title?: string;
  openInNewTab?: boolean;
  asChild?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const baseStyles = 'relative inline-block max-w-full';

const colorStyles = {
  light:
    'text-solar-text-primary hover:text-solar-surface-tertiary focus:text-solar-surface-tertiary',
  dark: 'text-solar-text-inverted-primary hover:text-solar-text-inverted-secondary focus:text-solar-text-inverted-secondary',
};

const variantStyles = {
  default: styles.default,
  underlined: styles.underlined,
};

const transitionStyle = 'transition-colors duration-600 ease-out';

function DefaultLink({
  href,
  children,
  title,
  className,
  rel,
  target,
  size,
  onClick,
}: {
  href: string;
  title: string;
  rel?: string;
  target?: string;
  children: React.ReactNode;
  className: string;
  size: 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <Link
      href={href}
      role="link"
      tabIndex={0}
      title={title}
      target={target}
      rel={rel}
      className="flex"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick?.(e as unknown as React.MouseEvent<HTMLAnchorElement>);
        }
      }}
    >
      <Paragraph size={size} className={className}>
        {children}
      </Paragraph>
    </Link>
  );
}

export default function TextLink({
  children,
  href = '',
  className = '',
  variant = 'default',
  color = 'light',
  title = '',
  openInNewTab = false,
  asChild = false,
  size = 'sm',
  onClick,
}: TextLinkProps) {
  const linkClass = cn(
    colorStyles[color],
    variantStyles[variant],
    transitionStyle,
    baseStyles,
    className,
  );

  const linkRel = openInNewTab ? 'noopener noreferrer' : undefined;
  const linkTitle = title || children?.toString() || '';
  const linkTarget = openInNewTab ? '_blank' : undefined;
  const Comp = asChild ? Slot : DefaultLink;

  return (
    <Comp
      href={href}
      title={linkTitle}
      className={linkClass}
      rel={linkRel}
      target={linkTarget}
      size={size}
      onClick={onClick}
    >
      {children}
    </Comp>
  );
}
